/**
 * Type definitions for AI Generation Service
 * Frontend types matching backend GenerationRequest and GenerationResult
 */

import { ResponsivePosition } from '@/types';

/**
 * Template component structure
 */
export interface TemplateComponent {
  id: string;
  type: string;
  properties?: Record<string, any>;
  style?: Record<string, any>;
  position?: { x: number; y: number; z: number };
  size?: { width: string | number; height: number };
  responsivePosition?: ResponsivePosition;
  semanticRole?: string;
  children?: string[];
}

/**
 * Request for AI page generation
 */
export interface GenerationRequest {
  description: string;
  projectId?: string;
  pageId?: string;
  constraints?: {
    maxComponents?: number;
    requiredComponentTypes?: string[];
    excludedComponentTypes?: string[];
    targetBreakpoints?: string[];
  };
  context?: {
    existingPages?: string[];
    userPreferences?: Record<string, any>;
    domainContext?: string;
  };
}

/**
 * Metadata about the generation process
 */
export interface GenerationMetadata {
  selectedTemplates: string[];
  tokensUsed: {
    step1: number;
    step2: number;
    total: number;
  };
  latency: {
    step1: number;
    step2: number;
    total: number;
  };
  confidence: number;
  reasoning: string;
  // Step 3 & 4 metadata (layout optimization & visual styling)
  step3Complete?: boolean;
  step4Complete?: boolean;
  step3Latency?: number;
  step4Latency?: number;
  totalLatency?: number;
  step1Latency?: number;
  step2Latency?: number;
}

/**
 * Generated page structure
 */
export interface GeneratedPage {
  id: string;
  name: string;
  components: TemplateComponent[];
}

/**
 * Generation error details
 */
export interface GenerationError {
  code: string;
  message: string;
  step: 1 | 2 | 3 | 4;
}

/**
 * Complete generation result from API
 */
export interface GenerationResult {
  success: boolean;
  page?: GeneratedPage;
  metadata?: GenerationMetadata;
  error?: GenerationError;
}

/**
 * API response wrapper (matching backend response format)
 */
export interface GenerationAPIResponse {
  success: boolean;
  data?: {
    page: GeneratedPage;
    metadata: GenerationMetadata;
  };
  error?: string;
  code?: string;
  step?: 1 | 2;
  meta?: {
    requestId: string;
    responseTime: number;
  };
}

/**
 * Generation loading states for UI
 */
export type GenerationStage =
  | 'idle'
  | 'preparing'
  | 'selecting'
  | 'generating'
  | 'complete'
  | 'error'
  | null;

/**
 * Generation state for store
 */
export interface GenerationState {
  isGenerating: boolean;
  stage: GenerationStage;
  metadata: GenerationMetadata | null;
  error: string | null;
  progress?: {
    step: 1 | 2;
    message: string;
  };
}

/**
 * Intent Classification Types
 */

/**
 * Intent categories for routing AI requests
 */
export enum IntentCategory {
  PAGE_GENERATION = 'PAGE_GENERATION',
  FORM_CREATION = 'FORM_CREATION',
  COMPONENT_MODIFICATION = 'COMPONENT_MODIFICATION',
  PROPERTY_CHANGE = 'PROPERTY_CHANGE',
  UNCLEAR = 'UNCLEAR',
}

/**
 * Context for intent classification
 */
export interface ClassificationContext {
  hasSelectedComponent?: boolean;
  isCanvasEmpty?: boolean;
  componentType?: string;
}

/**
 * Intent classification result
 */
export interface ClassificationResult {
  intent: IntentCategory;
  confidence: number; // 0-1
  reasoning: string;
  cached?: boolean;
  latency?: number;
}

/**
 * API response for intent classification
 */
export interface ClassificationAPIResponse {
  success: boolean;
  intent?: IntentCategory;
  confidence?: number;
  reasoning?: string;
  cached?: boolean;
  latency?: number;
  error?: string;
  message?: string;
  code?: string;
}
